import { Card } from '@/scopes/common/components/Card'

import { Price } from '@/scopes/search/search/pages/SearchPage/components/Results/ProductCard/ProductCardSplitDetails/Price'
import {
  PriceLoading,
} from '@/scopes/search/search/pages/SearchPage/components/Results/ProductCard/ProductCardSplitDetails/PriceLoading'
import { space, tok, tokPx } from '@sunwebgroup/ui-components'
import { focusOutline, Skeleton } from '@sunwebgroup/ui-components/uds'
import { ReactElement } from 'react'
import { css, styled } from 'styled-components'
import { ImageCarousel } from '../ImageCarousel'

const StyledProductCardSkeleton = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: ${tok('semantics_content_color')};
  position: relative;

  @media (min-width: 800px){
    flex-direction: row;
  }
  ${focusOutline()}
  ${ImageCarousel.modPartialImages(true)}
`

const Media = styled.div`
  display: flex;
  padding-top: ${space('primitives_space_12')};
  width: 100%;
  height: 170px;
  align-items: flex-start;
  flex-shrink: 0;
`

const ImageCarouselSkeleton = styled(Skeleton)`
  width: 100%;
  height: 100%;
`

ProductCardSplitDetailsLoading.modSize = (minWidth: 'tablet' | 'desktop') => {
  switch (minWidth) {
    case 'tablet':
      return css`
        ${Media} {
          width: 240px;
          height: 362px;
          padding-top: initial;
        }
        ${ImageCarousel.modPartialImages(false)}
      `
    case 'desktop':
      return css`
        ${Media} {
          width: 320px;
          height: 362px;
        }
        ${ImageCarousel.modPartialImages(false)}
      `
  }
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${space('primitives_space_6')}  ${space('primitives_space_6')}  ${space('primitives_space_6')};
  align-items: flex-start;
  gap: ${space('primitives_space_16')};
  flex: 1 0 0;

  @media (min-width: 800px){
    flex-direction: row;
    padding: ${space('primitives_space_12')}  ${space('primitives_space_12')}  ${space('primitives_space_12')}  ${space('primitives_space_16')};
  }

  ${focusOutline()}
`

const Main = styled.div`
  display: flex;
  padding: ${space('primitives_space_6')};
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  align-self: stretch;
  gap: ${space('primitives_space_16')};
`

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`

const ProductNameSkeleton = styled(Skeleton)`
  width: 211px;
  height: ${space('primitives_space_24')};
`
const LocationsSkeleton = styled(Skeleton)`
  width: 173px;
  height: ${space('primitives_space_16')};;
`

const UserRatingSkeleton = styled(Skeleton)`
  width: 65px;
  height: ${space('primitives_space_24')};
  border-radius: ${tokPx('primitives_corner_radius_xxl')};
`

const USPListSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_8')};
`

const USPItemSkeleton = styled(Skeleton)`
  width: 258px;
  height: ${space('primitives_space_16')};
`

const Configuration = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`

const ConfigurationItem = styled.div`
  display: flex;
  padding: ${space('primitives_space_4')} 0;
  gap: ${space('primitives_space_12')};
  align-items: center;
  color: ${tok('semantics_content_color')};
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  align-content: flex-start;
  gap: ${space('primitives_space_8')};
  align-self: stretch;
  flex-wrap: wrap;
`
const ViewButtonSkeleton = styled(Skeleton)`
  flex: 1 0 0;
  width: 100%;
  @media (min-width: ${tokPx('primitives_breakpoint_m_min')}) {
    width: unset;
  }
`

const Aside = styled.div`
  display: flex;
  padding: ${space('primitives_space_16')};
  flex-wrap: wrap;
  border-radius: ${tokPx('primitives_corner_radius_m')};
  background: ${tok('semantics_views_tinted_background_color')};
  justify-content: space-between;
  width: 100%;

  ${Configuration} {
    flex: 0 0 auto;
    order: 0;
  }
  ${Price} {
    flex: 0 0 auto;
    order: 1;
  }
  ${Actions} {
    flex-basis: 100%;
    order: 2;
    margin-top: 16px;
  }

  @media (min-width: 800px){
    width: 240px;
    flex-direction: column;
    align-items: center;
    gap: ${space('primitives_space_16')};
    height: 338px;

    ${Configuration} {
      order: 1;
    }
    ${Price} {
      order: 0;
    }
    ${Actions} {
      margin-top: initial;
      flex-basis: auto;
    }
  }
`

export function ProductCardSplitDetailsLoading(): ReactElement {
  return (
    <StyledProductCardSkeleton qa-product-card="true" data-qa="product-card">
      <Media>
        <ImageCarouselSkeleton />
      </Media>
      <Content>
        <Main>
          <HeaderGroup>
            <ProductNameSkeleton />
            <LocationsSkeleton />
          </HeaderGroup>
          <UserRatingSkeleton />
          <USPListSkeleton>
            <USPItemSkeleton />
            <USPItemSkeleton />
            <USPItemSkeleton />
          </USPListSkeleton>
        </Main>
        <Aside>
          <PriceLoading />
          <Configuration>
            <ConfigurationItem>
              <Skeleton />
            </ConfigurationItem>
            <ConfigurationItem>
              <Skeleton />
            </ConfigurationItem>
            <ConfigurationItem>
              <Skeleton />
            </ConfigurationItem>
            <ConfigurationItem>
              <Skeleton />
            </ConfigurationItem>
          </Configuration>
          <Actions>
            <ViewButtonSkeleton />
          </Actions>
        </Aside>
      </Content>
    </StyledProductCardSkeleton>
  )
}

ProductCardSplitDetailsLoading.displayName = 'ProductCardVariant417'
